import React, { useCallback, useEffect, useState, Fragment } from 'react'
import { useSelector } from 'react-redux'
import StatelessDataTable from '../../../../../app/components/statelessDataTable'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import createColumns from '../columns/columns'
import { filterStateList, apiAsync } from '../../../../../common/helpers'
import LEGACY_PRODUCTS from '../../../../../common/enum/legacyProducts'
import { SESSION_STORAGE_KEYS } from '../../../../../app/enums'
import locationService from '../../../../../services/locationService'
import wageService from '../../../../../services/wageService'
import { useLocations } from '../../../../../context/locationsContext'
import addressTypes from '../../../../../constants/addressTypes'
import jurisdictionService from '../../../../../services/jurisdictionService'
import MaintenanceBanner from '../../../../../account/components/maintenance/maintenanceBanner'
import { CSVDownloadError } from '../../../../../common/components/CSVDownloadError/CSVDownloadError'

const RemoteMinimumWageLocations = ({ companyId, tippedEmployees }) => {
  const [loading, setLoading] = useState(Boolean(companyId))
  const [data, setData] = useState([])
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)
  const [error, setError] = useState(false)
  const [facets, setFacets] = useState({})
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'displayName',
    direction: 'asc'
  })
  const sessionCompany = useSelector((state) => state.account.company)
  const locationsContext = useLocations()
  // FOR MAINTENANCE BANNER
  const [maintenanceBanner, setMaintenanceBanner] = useState(false)
  const [ignoreMaintenanceBannerFlag, setIgnoreMaintenanceBannerFlag] = useState()
  const ignoreMaintenanceBannerKey = SESSION_STORAGE_KEYS.IGNORE_MAINTENANCE_BANNER
  const sessionIgnoreMaintenanceBanner = JSON.parse(sessionStorage.getItem(ignoreMaintenanceBannerKey))

  const initialFilterOptions = {
    filter: '',
    field: ''
  }

  const [filterOptions, setFilterOptions] = useState(initialFilterOptions)
  const [filters, setFilters] = useState({ state: '' })

  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    filterOptions,
    eventHandlers: {
      setSortOptions,
      setFilterOptions,
      setPageNumber
    },
    tippedEmployees
  }))
  const [locales, setLocales] = useState([])

  const ROWS_PER_PAGE = 20

  const handleClose = () => setErrorPopupOpen(false)

  useEffect(() => {
    // FOR MAINTENANCE BANNER
    setIgnoreMaintenanceBannerFlag(sessionIgnoreMaintenanceBanner)
    let subscribed = true
    return (() => (subscribed = false))
  }, [companyId])

  useEffect(() => {
    let subscribed = true
    if (!sessionCompany) return

    // FOR MAINTENANCE BANNER
    // setMaintenanceBanner(true)

    populateTable({ pageNumber, filters, searchText, sortOptions, filterOptions, companyId, tippedEmployees })
      .catch((populateTableError) => {
        console.error('minwage location table -> populateTable threw error', populateTableError)
        throw populateTableError
      })

    return (() => (subscribed = false))
  }, [pageNumber, filters, searchText, sortOptions, filterOptions, companyId, tippedEmployees, sessionCompany])

  useEffect(() => {
    let subscribed = true

    // this if block defaults the "Filter By Locale" control to the full list of states until the locationsProvider loads the world
    // in a sane world we wouldn't do this, but here we are
    if (sessionCompany && !locationsContext.locationsData) {
      if (subscribed) {
        setLocales(filterStateList(LEGACY_PRODUCTS.MINWAGE)
          .map((legacyDto) => ({
            facet: {},
            locale: legacyDto
          })))
      }
    }

    if (sessionCompany && locationsContext.locationsData) {
      assignLocaleFilterOptions({
        subscribed,
        locations: Object.values(locationsContext.locationsData)
      })
    }

    return (() => (subscribed = false))
  }, [locationsContext.locationsData, sessionCompany])

  /**
   * Currently this is wired into some legacy garbage, specifically the "stateless" table controls, thus the goofy mapping.
   */
  const assignLocaleFilterOptions = ({ subscribed, locations }) => {
    const localeSet = locations.reduce((_set, location) => _set.add(location.state), new Set())

    const localeList = filterStateList(LEGACY_PRODUCTS.MINWAGE)
      .filter((legacyDto) => localeSet.has(legacyDto.state))
      .map((legacyDto) => ({
        facet: {},
        locale: legacyDto
      }))

    if (subscribed) setLocales(localeList)
  }

  const populateTable = async ({ pageNumber, filters, searchText, sortOptions, companyId, tippedEmployees }) => {
    const locationsApiResult = await locationService.getGeoLocations({
      companyId,
      pageNumber,
      pageSize: ROWS_PER_PAGE,
      products: ['minimumWage'],
      searchText,
      sort: sortOptions.field,
      sortOrder: sortOptions.direction,
      stateCodeFilter: filters && filters.state && filters.state.locale && filters.state.locale.state,
      includeProperties: ['wageSchedules', 'legacy']
    })

    if (locationsApiResult.statusCode !== 200) throw new Error('error fetching locations in populateTable')
    const locationsApiData = locationsApiResult.body.data

    const locationsWithRelevantWages = locationsApiData.locations.map((location) => ({
      ...location,
      wageSchedules: wageService.getIndustryApplicableWageSchedule(location.wageSchedules, sessionCompany)
    })).map((location) => ({
      ...location,
      currentWages: getCurrentWages(location.wageSchedules)
    }))

    // This list of jurisdiction unique names will be empty if the location does not have any wage information populated.
    // It comes from the  currentWages information on the location NOT the list of geoLocations tied to the location.
    const jdUniqueNames = locationsWithRelevantWages.reduce((jdUniqueNameList, location) => {
      if (location.currentWages.standardWage) jdUniqueNameList.add(location.currentWages.standardWage.jurisdictionUniqueName)
      if (location.currentWages.tippedWage) jdUniqueNameList.add(location.currentWages.tippedWage.jurisdictionUniqueName)
      return jdUniqueNameList
    }, new Set())

    const jdUniqueNameToDocDict = await jurisdictionService.getJdUniqueNameToDocDict(Array.from(jdUniqueNames))
    const legacyTableRows = mapLocationsToLegacyTable(locationsWithRelevantWages, jdUniqueNameToDocDict)

    setTotal(locationsApiData.total)
    setColumns(createColumns({
      sortOptions,
      eventHandlers: {
        setSortOptions,
        setFilterOptions,
        setPageNumber
      },
      tippedEmployees
    }))
    setPageNumber(pageNumber)
    setData(legacyTableRows)
    setLoading(false)
  }

  const mapLocationsToLegacyTable = (locations, jdUniqueNameToDocDict) => locations.map((location) => {
    const hasMultipleTippedWage = Boolean(jdUniqueNameToDocDict[location.currentWages.standardWage.jurisdictionUniqueName]
      && jdUniqueNameToDocDict[location.currentWages.standardWage.jurisdictionUniqueName].hasMultipleTippedWage)

    const mapToLegacyWageString = (wageDoc) => {
      const jdDoc = jdUniqueNameToDocDict[wageDoc.jurisdictionUniqueName]
      if (!wageDoc.rate) {
        return 'TBD'
      }
      const isTwoDigitDecimal = ((wageDoc.rate * 100) % 1 < 0.00001) || ((wageDoc.rate * 100) % 1 > 0.999999) // because sometimes doubles get stored stupidly
      const wageText = isTwoDigitDecimal ? `${wageDoc.rate.toFixed(2)}` : `${wageDoc.rate}`
      return wageDoc.rate ? `$${wageText} - ${jdDoc.type}` : 'TBD'
    }

    const shippingAddress = location.addresses.find((address) => address.type === addressTypes.SHIPPING)
    const addressComponents = [shippingAddress.address1, shippingAddress.address2]
    if (shippingAddress.address3) addressComponents.push(shippingAddress.address3)
    const streetAddress = addressComponents.join(' ')
      .concat(` ${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.zip} ${shippingAddress.country}`)

    return {
      address: streetAddress,
      addressObject: shippingAddress,
      displayName: location.displayName,
      id: location._id,
      jurisdictionName: location.currentWages.standardWage.jurisdictionUniqueName && jdUniqueNameToDocDict[location.currentWages.standardWage.jurisdictionUniqueName] && jdUniqueNameToDocDict[location.currentWages.standardWage.jurisdictionUniqueName].name,
      minWage: mapToLegacyWageString(location.currentWages.standardWage),
      tippedMinWage: hasMultipleTippedWage ? 'See location details' : mapToLegacyWageString(location.currentWages.tippedWage)
    }
  })

  const getCurrentWages = (relevantIndustryWageSchedules) => {
    if (!relevantIndustryWageSchedules || (Array.isArray(relevantIndustryWageSchedules) && relevantIndustryWageSchedules.length === 0)) {
      return { tippedWage: {}, standardWage: {} }
    }

    const now = new Date()
    const tippedWage = wageService.getEffectiveWageForDate(relevantIndustryWageSchedules.tippedSchedule, now)
    const standardWage = wageService.getEffectiveWageForDate(relevantIndustryWageSchedules.standardSchedule, now)

    return {
      tippedWage,
      standardWage
    }
  }

  const handleChangePage = useCallback((event) => {
    setPageNumber(event.selected + 1)
    setData([])
    setLoading(true)
  }, [])

  const handleSetSearchText = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    setSortOptions(value)
  }, [])

  const handleSetFilters = useCallback((value) => {
    setFilters(value)
    setPageNumber(1)
  }, [])

  const handleSetPageNumber = useCallback((value) => {
    setPageNumber(value)
  }, [])

  const handleExportCSV = async () => {
    setLoading(true)

    try {
      const locationResults = await apiAsync({
        api: `external/companies/${companyId}/locations/export`,
        params: {
          product: 'minimumWage',
          searchText,
          sortField: sortOptions.field,
          sortDirection: sortOptions.direction,
          stateCodeFilter: filters && filters.state && filters.state.locale && filters.state.locale.state
        },
        method: 'GET'
      })

      if (locationResults && locationResults.downloadUrl) {
        window.location.assign(locationResults.downloadUrl)
      } else {
        setErrorPopupOpen(true)
        throw Error('csv generation error')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    error
      ? <DefaultErrorMessage error={error} data-testid="error-container" />
      : maintenanceBanner && !ignoreMaintenanceBannerFlag
        ? <MaintenanceBanner />
        : (
          <Fragment>
            <StatelessDataTable {...{
              pageCount: Math.ceil(total / ROWS_PER_PAGE),
              isLoading: loading,
              showSearch: true,
              showExport: true,
              showLocaleFilter: true,
              recordType: 'Locations',
              eventHandlers: {
                handleChangePage,
                setSearchText: handleSetSearchText,
                setSortOptions: handleSetSortOptions,
                setFilters: handleSetFilters,
                export: handleExportCSV,
                setPageNumber: handleSetPageNumber
              },
              columns,
              data,
              filters,
              sortOptions,
              filterOptions,
              facets,
              pageNumber,
              total,
              searchText,
              rowsPerPage: ROWS_PER_PAGE,
              stateListFiltered: filterStateList(LEGACY_PRODUCTS.MINWAGE)
                .map((state) => state.state),
              locales
            }}
            />
            <CSVDownloadError open={errorPopupOpen} onClose={handleClose} />
          </Fragment>
        )
  )
}

export default RemoteMinimumWageLocations
